
import { useState, useEffect } from 'react';
import { formatCurrency2, formatPercent2, formatNumber2 } from '../utils/format';
import { sanitizeNumericInput } from '../utils/numberInput';

interface InputGroupProps {
  label: any;
  value: number;
  onChange: (val: number) => void;
  type?: 'currency' | 'percent' | 'number';
  step?: number;
  min?: number;
  max?: number;
}

const InputGroup = (props: InputGroupProps) => {
  const { label, value, onChange, type = 'number', step = 1, min = 0, max } = props;
  // Fix: Use a local string state to manage the raw input and strip leading zeros
  const [localValue, setLocalValue] = useState(value.toString());

  // Keep local state in sync when external value changes (but not when we're focused)
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const displayValue = (v: number) => {
    if (type === 'currency') return formatCurrency2(v);
    if (type === 'percent') return formatPercent2(v);
    return formatNumber2(v);
  };

  const handleInputChange = (e: any) => {
    const sanitized = sanitizeNumericInput(e.target.value);
    setLocalValue(sanitized);

    // Immediately propagate the numeric value
    const numeric = parseFloat(sanitized);
    if (!isNaN(numeric)) {
      onChange(numeric);
    }
  };

  return (
    <div className="mb-6 w-full group">
      <div className="flex items-center justify-between mb-2.5">
        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] group-hover:text-emerald-500 transition-colors">
          {label}
        </label>
        <span className="text-[10px] text-emerald-500 font-black uppercase tracking-widest">{displayValue(value)}</span>
      </div>
      <div className="flex items-center gap-4 w-full">
        <div className="relative flex-grow h-2 bg-slate-700 rounded-full overflow-hidden">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e: any) => onChange(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          <div
            className="absolute inset-y-0 left-0 bg-emerald-500 transition-all duration-200"
            style={{ width: `${((value - (min || 0)) / ((max || value) - (min || 0))) * 100}%` }}
          />
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleInputChange}
          className="w-28 flex-shrink-0 px-3 py-2 text-xs border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 font-black text-white bg-slate-800 text-right shadow-sm transition-all"
        />
      </div>
      <div className="flex justify-between mt-2 px-0.5">
        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{displayValue(min || 0)}</span>
        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{max ? displayValue(max) : '∞'}</span>
      </div>
    </div>
  );
};

export default InputGroup;
